import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { z } from "zod";
import { Loader2, Printer } from "lucide-react";
import { captureGiftOrder } from "@/lib/gift.functions";
import { GiftCardArt } from "@/components/shop/GiftMemberships";
import type { GiftMonths } from "@/lib/gift-plans";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/shop/gift-thanks")({
  validateSearch: z.object({ token: z.string().optional() }),
  head: () => ({
    meta: [
      { title: "Thank you for your gift | Recovery Trauma™" },
      { name: "description", content: "Your Recovery Trauma™ Gift Membership is confirmed." },
      { property: "og:title", content: "Thank you for your gift | Recovery Trauma™" },
      { property: "og:description", content: "Your Recovery Trauma™ Gift Membership is confirmed." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: GiftThanks,
});

type Result = Awaited<ReturnType<typeof captureGiftOrder>>;

function GiftThanks() {
  const { token } = Route.useSearch();
  const capture = useServerFn(captureGiftOrder);
  const [res, setRes] = useState<Result | null>(null);

  useEffect(() => {
    if (!token) return;
    let tries = 0;
    let stop = false;
    const run = async () => {
      const r = await capture({ data: { orderId: token } }).catch((e) => ({ error: String(e) }) as Result);
      if (stop) return;
      if ("pending" in r && r.pending && tries++ < 5) return void setTimeout(run, 2500);
      setRes(r);
    };
    run();
    return () => {
      stop = true;
    };
  }, [token, capture]);

  if (!token) return <Msg title="No gift order found" body="If you've just paid, please check your email for your receipt." />;
  if (!res) {
    return (
      <main className="flex min-h-[50vh] flex-col items-center justify-center gap-3 px-4 text-center">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--gold-deep)]" aria-hidden />
        <p className="text-muted-foreground">Confirming your payment…</p>
      </main>
    );
  }
  if ("error" in res && res.error) return <Msg title="We couldn't confirm the payment yet" body={`${res.error} If money has left your account, please email info@recoverytrauma.com and we'll sort it out.`} />;
  if ("pending" in res || !("code" in res) || !res.code) return <Msg title="Payment still processing" body="Your voucher will be emailed as soon as PayPal confirms the payment." />;

  const sentTo = res.deliveryMode === "gift" ? res.recipientEmail : res.buyerEmail;
  return (
    <main className="mx-auto w-full max-w-xl px-4 py-10 text-center">
      <h1 className="font-display text-3xl font-semibold">Thank you 🎁</h1>
      <p className="mt-2 text-muted-foreground">
        The voucher has been emailed to <strong>{sentTo}</strong>. Your receipt has gone to {res.buyerEmail}.
      </p>
      <div className="mt-6">
        <GiftCardArt months={res.months as GiftMonths} />
      </div>
      <p className="mt-5 text-sm text-muted-foreground">Gift code</p>
      <p className="font-mono text-2xl font-bold tracking-wider">{res.code}</p>
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
        <Button asChild size="lg" className="rounded-full">
          <Link to="/gift/voucher/$code" params={{ code: res.code }}>
            <Printer className="me-2 h-4 w-4" aria-hidden /> Download / Print voucher
          </Link>
        </Button>
        <Button asChild variant="outline" size="lg" className="rounded-full">
          <Link to="/shop">Back to the shop</Link>
        </Button>
      </div>
    </main>
  );
}

function Msg({ title, body }: { title: string; body: string }) {
  return (
    <main className="mx-auto max-w-xl px-4 py-16 text-center">
      <h1 className="font-display text-2xl font-semibold">{title}</h1>
      <p className="mt-3 text-muted-foreground">{body}</p>
      <Link to="/shop" className="mt-6 inline-block text-[var(--gold-deep)] underline">Back to the shop</Link>
    </main>
  );
}
